import type { Message, Run } from "@playbookai/player-ai-client";
import type {
  PlayerAIChatMessage,
  PlayerAIMessageReference,
} from "@playbookai/ui";
import { isPlayerAIResponse } from "./thread-order.js";

/** Whose exchange a row belongs to under "My exchanges only" (Task 17). */
export type Exchange = "mine" | "other";

/** The first word of a trusted display name, for one-line attributions. */
export function firstName(
  displayName: string | null | undefined,
): string | undefined {
  return displayName?.trim().split(/\s+/)[0] || undefined;
}

function oneLine(text: unknown): string {
  return typeof text === "string" ? text.replace(/\s+/g, " ").trim() : "";
}

/**
 * Task 1 — identity for the "N new messages" count.
 *
 * A person's message counts by its id. A Player AI response counts by its Run
 * and its place among that Run's responses, which is exactly the key the
 * streamed draft of that response carries — so an answer counts once however
 * many chunks it arrived in, and the draft handing over to the stored message
 * is not a second arrival. Proposals, notices and activity carry no key.
 */
export function newMessageKeys(messages: readonly Message[]): {
  readonly byMessageId: ReadonlyMap<string, string>;
  readonly draftKey: (runId: string) => string;
} {
  const responsesByRun = new Map<string, number>();
  const byMessageId = new Map<string, string>();
  const ordered = [...messages].sort(
    (left, right) => left.sequence - right.sequence,
  );
  for (const message of ordered) {
    if (message.role === "user") {
      byMessageId.set(message.id, message.id);
    } else if (isPlayerAIResponse(message)) {
      if (message.run_id === null) {
        byMessageId.set(message.id, message.id);
        continue;
      }
      const ordinal = responsesByRun.get(message.run_id) ?? 0;
      responsesByRun.set(message.run_id, ordinal + 1);
      byMessageId.set(message.id, `run:${message.run_id}:${ordinal}`);
    }
  }
  return {
    byMessageId,
    draftKey: (runId) => `run:${runId}:${responsesByRun.get(runId) ?? 0}`,
  };
}

/**
 * Task 7 — the message that started the Run behind this response, as the
 * server stamped it when the response was written. Absent for a Run no
 * message started (an opening, a phase entry, a review with nothing asked).
 */
export function triggerPreviewOf(
  message: Message,
): PlayerAIChatMessage["triggerPreview"] {
  const trigger =
    message.role === "assistant" ? message.content.trigger : undefined;
  if (!trigger || typeof trigger.message_id !== "string") return undefined;
  return {
    messageId: trigger.message_id,
    sequence:
      typeof trigger.message_sequence === "number"
        ? trigger.message_sequence
        : null,
    text: oneLine(trigger.text),
    authorName: firstName(trigger.author_display_name) ?? null,
    additionalCount:
      typeof trigger.additional_count === "number" &&
      trigger.additional_count > 0
        ? trigger.additional_count
        : 0,
  };
}

/**
 * The same preview for a response still being written, where the live Run
 * names its one triggering message. A group Run's requests are known to the
 * server only, so its preview arrives with the stored response instead.
 */
export function liveTriggerPreview(
  run: Run | null,
  messagesById: ReadonlyMap<string, Message>,
): PlayerAIChatMessage["triggerPreview"] {
  if (run?.trigger_type !== "message" || !run.trigger_message_id) {
    return undefined;
  }
  const trigger = messagesById.get(run.trigger_message_id);
  if (!trigger) return undefined;
  return {
    messageId: trigger.id,
    sequence: trigger.sequence,
    text: oneLine(trigger.content.text),
    authorName: firstName(trigger.author_display_name) ?? null,
    additionalCount: 0,
  };
}

/**
 * Task 7 — the Player AI response a user message replies to. The stored
 * relationship names it by id and sequence only; the preview line reads the
 * response itself when it is loaded, and says what it is when it is not.
 */
export function replyToOf(
  message: Message,
  messagesById: ReadonlyMap<string, Message>,
): PlayerAIMessageReference | undefined {
  const reply = message.role === "user" ? message.content.reply_to : undefined;
  if (!reply || typeof reply.message_id !== "string") return undefined;
  const target = messagesById.get(reply.message_id);
  return {
    messageId: reply.message_id,
    sequence:
      typeof reply.message_sequence === "number"
        ? reply.message_sequence
        : null,
    text: oneLine(target?.content.text) || "Player AI response",
  };
}

/** Task 6 — the people a message mentions, for highlighting only. */
export function personMentionsOf(
  message: Message,
): PlayerAIChatMessage["personMentions"] {
  const people = (message.mentions ?? []).flatMap((mention) =>
    mention.type === "user" &&
    typeof mention.token === "string" &&
    mention.occurrence_indices?.length
      ? [
          {
            token: mention.token,
            occurrenceIndices: [...mention.occurrence_indices],
          },
        ]
      : [],
  );
  return people.length > 0 ? people : undefined;
}

/** Task 6 — does this message mention `userId`? */
export function mentionsUser(message: Message, userId: string): boolean {
  return (message.mentions ?? []).some(
    (mention) => mention.type === "user" && mention.user_id === userId,
  );
}

/**
 * Task 17 — the viewer's own messages are theirs; a Player AI response is
 * theirs when one of the messages its Run handled was. The relationship is
 * the one the server recorded on the response (Task 7), so a response that
 * answered several people shows for each of them, and one that answered
 * nobody's message — an opening, a phase entry — belongs to no one.
 */
export function exchangeOfMessage(
  message: Message,
  viewerId: string,
): Exchange | undefined {
  if (message.role === "user") {
    return message.author_user_id === viewerId ? "mine" : "other";
  }
  if (!isPlayerAIResponse(message)) return undefined;
  const authors = message.content.trigger?.author_user_ids;
  return Array.isArray(authors) && authors.includes(viewerId)
    ? "mine"
    : "other";
}

/** Task 17 — each answered Run's exchange, from its stored responses. */
export function runExchanges(
  messages: readonly Message[],
  viewerId: string,
): ReadonlyMap<string, Exchange> {
  const byRun = new Map<string, Exchange>();
  for (const message of messages) {
    if (message.run_id === null || !isPlayerAIResponse(message)) continue;
    const exchange = exchangeOfMessage(message, viewerId);
    if (exchange === "mine" || !byRun.has(message.run_id)) {
      byRun.set(message.run_id, exchange ?? "other");
    }
  }
  return byRun;
}

/**
 * Task 17 — the live Run's exchange before its response is written: the
 * viewer asked for it, or wrote in the input it was handed. The Run does not
 * project which requests it claimed, so this reads its input window; the
 * stored response settles it for good once it lands.
 */
export function liveRunExchange(
  run: Run | null,
  messages: readonly Message[],
  viewerId: string,
): Exchange | undefined {
  if (!run) return undefined;
  if (run.requested_by_user_id === viewerId) return "mine";
  const after = run.input_after_message_sequence ?? 0;
  return messages.some(
    (message) =>
      message.role === "user" &&
      message.author_user_id === viewerId &&
      message.sequence > after &&
      message.sequence <= run.input_through_message_sequence,
  )
    ? "mine"
    : "other";
}
